import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { activeClaimPerformedSelector } from '@redux/slices/claimStatusSlice'
import {
  authenticatedSelector,
  isAnonymousUserSelector,
  isGuestUserSelector,
  sessionReadySelector,
} from '@redux/slices/connectorSlice'
import {
  isInsuranceMemberNoBenefitsAvailable,
  isSyncInsuranceMemberEligibilityRequired,
} from '@utils/insuranceMemberUtil'
import { useClaimRecord } from '@hooks/useClaimRecord'
import { getInsuranceToggleState } from '@utils/insuranceToggleUtil'
import { ServiceStatus, ServiceType } from '../types'

type UseServiceStatusReturn = Record<ServiceType, ServiceStatus | undefined>

export const useServiceStatus = (): UseServiceStatusReturn => {
  const isGuest = useSelector(isGuestUserSelector)
  const isAnonymousUser = useSelector(isAnonymousUserSelector)
  const isAuthenticated = useSelector(authenticatedSelector)
  const isSessionReady = useSelector(sessionReadySelector)
  const performedActiveCheck = useSelector(activeClaimPerformedSelector)
  const { claimRecordId, claimRecord, claimRecordDeleted } = useClaimRecord()

  const validSession = (isAuthenticated || isGuest) && isSessionReady

  return useMemo<UseServiceStatusReturn>(() => {
    const getServiceStatus = (requireBenefits?: boolean): ServiceStatus | undefined => {
      if (isAnonymousUser) {
        return 'unavailable'
      }

      if (validSession && performedActiveCheck) {
        // if claim record exists, determine status from toggle state
        // otherwise service is unavailable if not synced
        if (claimRecordId && claimRecord) {
          if (!isSyncInsuranceMemberEligibilityRequired(claimRecord?.claimData?.useInsuranceStatus)) {
            const { toggleOn } = getInsuranceToggleState({ claimRecord, claimRecordDeleted })
            let isReady = toggleOn

            if (requireBenefits) {
              isReady = toggleOn && !isInsuranceMemberNoBenefitsAvailable(claimRecord.status)
            }

            return isReady ? 'ready' : 'unavailable'
          }
        } else if (!claimRecordId) {
          return 'unavailable'
        }
      }
    }

    const serviceStatus = getServiceStatus()
    const productQuoteServiceStatus = getServiceStatus(true)

    return {
      'benefit-plan': serviceStatus,
      'insurance-data': serviceStatus,
      'product-quote': productQuoteServiceStatus,
    }
  }, [isAnonymousUser, validSession, performedActiveCheck, claimRecordId, claimRecord, claimRecordDeleted])
}
